import React, { useEffect, useState, useCallback } from 'react';
import {
  Drawer, Box, IconButton, Typography, Stack, Chip,
  CircularProgress, Alert, Button, Snackbar, TextField, InputAdornment,
} from '@mui/material';
import { Close, Edit as EditIcon, Search as SearchIcon, ArrowBack } from '@mui/icons-material';
import DrillDownBreadcrumb from './DrillDownBreadcrumb';
import FormulaView from './FormulaView';
import AggregateView from './AggregateView';
import TransactionView from './TransactionView';
import ManualClassificationModal from '../reconciliation/ManualClassificationModal';
import { drillDownPL, getJournalEntryDetail, searchOrders } from '../../../services/financialConfigService';

/**
 * Right-side drawer that renders P&L cell drill-downs.
 *
 * Props:
 *   open       — boolean
 *   target     — { row_id, period, drill_path: [{dim, value, label}, ...] } | null
 *   filters    — { brand_id, start_date, end_date, market, channel } from PLReport
 *   plPayload  — full plData object so the formula handler can derive components
 *                without re-querying ClickHouse for level 1 of calc rows
 *   onClose    — () => void
 *   onNavigate — (next_target) => void  (parent updates `target` and URL)
 */
export default function DrillDownDrawer({
  open, target, filters, plPayload, onClose, onNavigate,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Phase 4: inline reclassify (Pending Queue rows only)
  const [reclassifyTxn, setReclassifyTxn] = useState(null);
  // Phase 5: JE-level reclassify from any drilldown that surfaces journal entries
  const [reclassifyJe, setReclassifyJe] = useState(null);
  const [snack, setSnack] = useState(null);

  // Order ID search — bypasses the drill path. When `searchData` is set,
  // the drawer body renders that result instead of the normal drill data.
  const [searchInput, setSearchInput] = useState('');
  const [searchData, setSearchData] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchLevel = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    setError(null);
    try {
      const result = await drillDownPL({
        row_id: target.row_id,
        period: target.period,
        drill_path: target.drill_path || [],
        filters,
        pl_payload: plPayload,
      });
      if (result && result.success) setData(result.data);
      else setError(result?.error || 'Drill-down failed');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Drill-down failed');
    } finally {
      setLoading(false);
    }
  }, [target, filters, plPayload]);

  // Load-more: fetch the next page of rows at the current drill level using
  // the offset reported by the previous response. Backend caps page size to
  // 500; large drills should use Download-all CSV instead of clicking many times.
  const loadMore = useCallback(async () => {
    if (!target || !data) return;
    if (loadingMore) return;
    if (!data.has_more) return;
    setLoadingMore(true);
    try {
      const result = await drillDownPL({
        row_id: target.row_id,
        period: target.period,
        drill_path: target.drill_path || [],
        filters,
        pl_payload: plPayload,
        limit: 500,
        offset: data.next_offset || (data.rows?.length || 0),
      });
      if (result && result.success && Array.isArray(result.data?.rows)) {
        setData(prev => ({
          ...prev,
          rows: [...prev.rows, ...result.data.rows],
          has_more: !!result.data.has_more,
          next_offset: result.data.next_offset,
        }));
      } else {
        setSnack({ msg: result?.error || 'Load more failed', severity: 'error' });
      }
    } catch (err) {
      setSnack({ msg: err?.response?.data?.error || err.message || 'Load more failed', severity: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }, [target, filters, plPayload, data, loadingMore]);

  // Fetch on open / target / drill_path change
  useEffect(() => {
    if (open && target) fetchLevel();
    if (!open) setData(null);
    // Phase 4: drawer or target change clears any orphan reclassify state.
    // Without this, a stale reclassifyTxn from a previous session re-opens the
    // modal the next time the drawer is opened.
    setReclassifyTxn(null);
    setReclassifyJe(null);
    // Drawer reopening or drilling further clears the order-search overlay.
    setSearchData(null);
    setSearchError(null);
    setSearchInput('');
  }, [open, target?.row_id, target?.period, JSON.stringify(target?.drill_path || []), fetchLevel]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleOrderSearch = useCallback(async () => {
    const q = (searchInput || '').trim();
    if (q.length < 2) return;
    setSearching(true);
    setSearchError(null);
    try {
      const result = await searchOrders({ query: q, filters });
      if (result?.success) setSearchData(result.data);
      else setSearchError(result?.error || 'Search failed');
    } catch (err) {
      setSearchError(err?.response?.data?.error || err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchInput, filters]);

  const clearSearch = () => {
    setSearchData(null);
    setSearchError(null);
    setSearchInput('');
  };

  // Full-dataset CSV download: re-fetch the same drill with csvExport=true so
  // the backend returns up to 100k rows (vs the on-screen 100/500 cap).
  // The fetch is fired here, then the resulting rows are CSV-serialized in JS
  // using the same column logic as the on-screen TransactionView/AggregateView.
  const downloadAllCsv = useCallback(async () => {
    if (!target) return;
    try {
      const result = await drillDownPL({
        row_id: target.row_id,
        period: target.period,
        drill_path: target.drill_path || [],
        filters,
        pl_payload: plPayload,
        csvExport: true,
      });
      if (!result?.success || !result.data?.rows) {
        setSnack({ msg: result?.error || 'CSV export failed', severity: 'error' });
        return;
      }
      const rows = result.data.rows;
      // Pick a column set: order rows have the rich set; aggregate rows have label/amount.
      const candidates = [
        'date', 'order_id', 'order_name', 'brand_name', 'channel',
        'store_or_retailer', 'warehouse_code', 'invoice_number',
        'payment_gateway', 'native_currency', 'native_amount', 'fx_rate',
        'cogs_native', 'shipping_native', 'handling_native', 'payment_fees_native',
        'taxes_native', 'refund_native',
        'bank_memo', 'line_memo', 'je_memo',
        'label', 'count', 'amount_usd',
      ];
      const header = candidates.filter(h => rows.some(r => r[h] != null && r[h] !== ''));
      const lines = [header.join(',')];
      rows.forEach(r => {
        lines.push(header.map(h => {
          const v = r[h] ?? '';
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        }).join(','));
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(result.data.title || 'drill').replace(/[^a-z0-9-]+/gi, '_')}_full.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnack({ msg: `Downloaded ${rows.length} rows`, severity: 'success' });
    } catch (err) {
      setSnack({ msg: err?.response?.data?.error || err.message || 'CSV export failed', severity: 'error' });
    }
  }, [target, filters, plPayload]);

  const handleAggregateRowClick = (row) => {
    if (!row.drillable) return;
    const next_dim = row.next_dim || 'value';
    const next_path = [
      ...((target && target.drill_path) || []),
      { dim: next_dim, value: row.id, label: row.label },
    ];
    onNavigate({ ...target, drill_path: next_path });
  };

  const handleCrumbClick = (index) => {
    // crumb 0 = root row (the row_id itself); subsequent crumbs are drill_path entries.
    // Clicking crumb i means: keep drill_path entries before i.
    // crumb 0 → drill_path = [] (root)
    // crumb 1 → drill_path keeps 0 entries (the row_id itself, no drill yet)
    // crumb N → drill_path keeps N-1 entries
    const new_path = ((target && target.drill_path) || []).slice(0, Math.max(0, index - 1));
    onNavigate({ ...target, drill_path: new_path });
  };

  const handleFormulaComponentClick = (component) => {
    if (!component.drillable) return;
    onNavigate({ row_id: component.row_id, period: target.period, drill_path: [] });
  };

  // Phase 4: only the Pending Queue Level 2 (transactions view) gets a reclassify action.
  // Other transaction views (orders, refunds, JE lines, cash transactions) are read-only.
  const showReclassify = (
    data?.level_type === 'transactions' &&
    target?.row_id === 'pending_queue_estimate' &&
    (target?.drill_path?.length || 0) > 0
  );

  const reclassifyAction = showReclassify
    ? (row) => {
        // Skip if this row doesn't have the bank_transaction_id we need.
        if (!row.bank_transaction_id) return null;
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon style={{ fontSize: 14 }} />}
            onClick={(e) => {
              e.stopPropagation();
              // The classify endpoint expects bank_transaction_id (NOT queue_id) as the URL param.
              // Build the modal's transaction prop with explicit field names.
              setReclassifyTxn({
                id: row.bank_transaction_id,
                bank_transaction_id: row.bank_transaction_id,
                bank_description: row.bank_description,
                bank_amount: row.bank_amount,
                bank_currency: row.bank_currency,
                bank_source: row.bank_source,
                bank_transaction_date: row.date,
              });
            }}
          >
            Reclassify
          </Button>
        );
      }
    : null;

  // Phase 5: JE-level reclassify. Surfaces a button on aggregate rows whose
  // next_dim is journal_entry_id (today: OpEx Level 1). Click → fetch JE
  // detail (current accounts, AI suggestion if any), then open the modal in
  // reclassify mode. The modal POSTs to /api/finance/journal-entries/:id/reclassify
  // which voids the old JE and creates a fresh one.
  const handleReclassifyJeClick = useCallback(async (row) => {
    try {
      const result = await getJournalEntryDetail(row.id);
      if (result?.success && result.data) {
        setReclassifyJe(result.data);
      } else {
        setSnack({ msg: result?.error || 'Could not load journal entry', severity: 'error' });
      }
    } catch (err) {
      setSnack({
        msg: err?.response?.data?.error || err.message || 'Could not load journal entry',
        severity: 'error',
      });
    }
  }, []);

  const aggregateRowAction = (
    data?.level_type === 'aggregate' &&
    Array.isArray(data?.rows) &&
    data.rows.some(r => r.next_dim === 'journal_entry_id')
  )
    ? (row) => {
        if (row.next_dim !== 'journal_entry_id') return null;
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon style={{ fontSize: 14 }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleReclassifyJeClick(row);
            }}
            sx={{ ml: 1 }}
          >
            Reclassify
          </Button>
        );
      }
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 720 } } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {data?.title || (target ? target.row_id : 'Drill-down')}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              {filters?.brand_id != null && (
                <Chip size="small" label={`Brand ${filters.brand_id}`} variant="outlined" />
              )}
              {filters?.brand_id == null && (
                <Chip size="small" label="All Brands" variant="outlined" />
              )}
              {filters?.market && (
                <Chip size="small" label={filters.market} variant="outlined" />
              )}
              {filters?.channel && (
                <Chip size="small" label={filters.channel} variant="outlined" />
              )}
            </Stack>
            {!searchData && data?.breadcrumb && (
              <DrillDownBreadcrumb crumbs={data.breadcrumb} onCrumbClick={handleCrumbClick} />
            )}
            {searchData && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBack style={{ fontSize: 14 }} />}
                  onClick={clearSearch}
                  sx={{ textTransform: 'none', minWidth: 0, p: 0 }}
                >
                  Back to drill
                </Button>
                <Typography variant="caption" color="text.secondary">
                  · Search: {searchInput}
                </Typography>
              </Stack>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </Stack>

        {/* Order ID / order name search — works at any drill level. */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search by order ID or order name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleOrderSearch(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={searching || (searchInput || '').trim().length < 2}
            onClick={handleOrderSearch}
          >
            {searching ? '…' : 'Find'}
          </Button>
          {/* CSV-all: bypasses the screen 100/500 cap, fetches up to 100k rows */}
          {data && (data.level_type === 'aggregate' || data.level_type === 'transactions') && !searchData && (
            <Button
              size="small"
              variant="outlined"
              onClick={downloadAllCsv}
              title="Download all rows for the current drill (up to 100,000)"
            >
              Download all CSV
            </Button>
          )}
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {searchError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSearchError(null)}>
            {searchError}
          </Alert>
        )}
        {searchData ? (
          searchData.rows.length === 0 ? (
            <Box>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                No orders match "{searchInput}".
              </Typography>
              {(filters?.channel || filters?.market || filters?.brand_id) && (
                <Typography variant="caption" color="text.secondary">
                  Active filters narrow the search:
                  {filters?.brand_id ? ` brand ${filters.brand_id} ·` : ''}
                  {filters?.channel ? ` channel ${filters.channel} ·` : ''}
                  {filters?.market ? ` market ${filters.market} ·` : ''}
                  {' '}clear them on /finance/pl and retry if the order belongs elsewhere.
                </Typography>
              )}
            </Box>
          ) : (
            /* onReload matters here too: saving a VAT treatment now changes the LEDGER (books the
               2210 split), so the row's treatment and reclaimable amount only appear once the rows
               are refetched. Without it a save from the search overlay flipped the chip optimistically
               and then showed a stale blank reclaim until the user re-ran the search. */
            <TransactionView data={searchData} onReload={handleOrderSearch} />
          )
        ) : (
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!loading && error && (
              <Alert severity="error" action={<Button onClick={fetchLevel}>Retry</Button>}>
                {error}
              </Alert>
            )}
            {!loading && !error && data && data.level_type === 'formula' && (
              <FormulaView data={data} onComponentClick={handleFormulaComponentClick} />
            )}
            {!loading && !error && data && data.level_type === 'aggregate' && (
              <>
                {data.rows.length === 0 ? (
                  <Typography color="text.secondary">
                    No data found for this filter.
                  </Typography>
                ) : (
                  <AggregateView
                    data={data}
                    onRowClick={handleAggregateRowClick}
                    rowAction={aggregateRowAction}
                    onLoadMore={data.has_more ? loadMore : null}
                    loadingMore={loadingMore}
                  />
                )}
              </>
            )}
            {!loading && !error && data && data.level_type === 'transactions' && (
              <>
                {data.rows.length === 0 ? (
                  <Typography color="text.secondary">
                    No transactions found for this filter.
                  </Typography>
                ) : (
                  <TransactionView
                    data={data}
                    rowAction={reclassifyAction}
                    onLoadMore={data.has_more ? loadMore : null}
                    loadingMore={loadingMore}
                    onReload={fetchLevel}
                  />
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Phase 4: inline reclassify modal — opened from a Pending Queue row */}
      <ManualClassificationModal
        open={reclassifyTxn !== null}
        onClose={() => setReclassifyTxn(null)}
        onSave={() => {
          setReclassifyTxn(null);
          setSnack({
            msg: 'Reclassified — drill refreshed. Reload the P&L page to update the parent cell.',
            severity: 'success',
          });
          // Refetch the current drill — reclassified row now has a journal_entry_id
          // and drops out of the WHERE filter. Parent P&L cell stays stale until
          // the user reloads /finance/pl (the snackbar tells them so).
          fetchLevel();
        }}
        transaction={reclassifyTxn || undefined}
        suggestedClassification={null}
      />

      {/* Phase 5: JE-level reclassify modal — opened from any drilldown row whose
          next_dim is journal_entry_id. Backend voids the existing JE and inserts
          a new one with the new debit/credit/brand/memo. */}
      <ManualClassificationModal
        open={reclassifyJe !== null}
        onClose={() => setReclassifyJe(null)}
        mode="reclassify"
        journalEntryId={reclassifyJe?.je_id}
        currentValues={reclassifyJe ? {
          debit_account_id: reclassifyJe.current_debit_account_id,
          credit_account_id: reclassifyJe.current_credit_account_id,
          brand_id: reclassifyJe.brand_id,
          memo: reclassifyJe.memo,
          source_description: reclassifyJe.source_description,
          entry_date: reclassifyJe.entry_date,
          amount: reclassifyJe.amount,
          currency: reclassifyJe.currency,
          source_table: reclassifyJe.source_table,
        } : null}
        aiSuggestion={reclassifyJe?.ai_suggestion || null}
        sourceRule={reclassifyJe?.source_rule || null}
        onSave={(result) => {
          setReclassifyJe(null);
          let msg = 'Reclassified — drill refreshed. Reload the P&L page to update the parent cell.';
          if (result?.rule?.updated) {
            const ruleName = result.rule.ruleName || 'existing rule';
            msg = `Reclassified + rule "${ruleName}" re-pointed to new account. Future matches will route correctly. Reload the P&L page to update the parent cell.`;
          } else if (result?.rule?.created) {
            msg = 'Reclassified + new rule saved. Drill refreshed; reload the P&L page to update the parent cell.';
          } else if (result?.rule?.strengthened) {
            msg = 'Reclassified + existing rule strengthened. Drill refreshed; reload the P&L page to update the parent cell.';
          } else if (result?.rule?.error) {
            msg = `Reclassified, but rule save failed (${result.rule.error}). Drill refreshed.`;
          }
          setSnack({ msg, severity: 'success' });
          fetchLevel();
        }}
      />

      <Snackbar
        open={snack !== null}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Drawer>
  );
}
